import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Empty, Spin, Table, Tabs, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import axios from 'axios'

export interface AdhocRapportRow {
  id: string
  softaccess_id: string
  softaccess_name: string
  created_at: string
  creator_name: string
  sector_name: string
  status: string
}

interface SoftaccessGroup {
  softaccess_id: string
  softaccess_name: string
  rapports: AdhocRapportRow[]
}

interface OpenAdhocRapportsProps {
  orgaId: string
}

function formatDate(dateString: string) {
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return dateString
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

function uniqueFilterOptions(values: (string | null | undefined)[]) {
  return Array.from(new Set(values.filter((v): v is string => !!v))).map(value => ({
    text: value,
    value,
  }))
}

function getColumns(rapports: AdhocRapportRow[]): ColumnsType<AdhocRapportRow> {
  return [
    {
      title: 'Date de création',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a, b) => a.created_at.localeCompare(b.created_at),
      defaultSortOrder: 'descend',
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Créateur du document',
      dataIndex: 'creator_name',
      key: 'creator_name',
      sorter: (a, b) => a.creator_name.localeCompare(b.creator_name),
      filterSearch: true,
      filters: uniqueFilterOptions(rapports.map(r => r.creator_name)),
      onFilter: (value, record) => record.creator_name === value,
      render: (value: string) => value || '-',
    },
    {
      title: 'Nom secteur',
      dataIndex: 'sector_name',
      key: 'sector_name',
      sorter: (a, b) => a.sector_name.localeCompare(b.sector_name),
      filterSearch: true,
      filters: uniqueFilterOptions(rapports.map(r => r.sector_name)),
      onFilter: (value, record) => record.sector_name === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [{ text: 'Supprimé', value: 'deleted' }, { text: '-', value: '' }],
      onFilter: (value, record) => record.status === value,
      render: (value: string) => (value === 'deleted' ? 'Supprimé' : '-'),
    },
  ]
}

export function OpenAdhocRapports({ orgaId }: OpenAdhocRapportsProps) {
  const [rapports, setRapports] = useState<AdhocRapportRow[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined)

  const loadRapports = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get<AdhocRapportRow[]>(
        `/organisations/${orgaId}/etudesadhoc/rapports/list`,
      )
      setRapports(data)
    } catch (error) {
      console.error(error)
      message.error('Impossible de charger les rapports ad-hoc')
    } finally {
      setLoading(false)
    }
  }, [orgaId])

  useEffect(() => {
    loadRapports()
  }, [loadRapports])

  const groups = useMemo<SoftaccessGroup[]>(() => {
    const bySoftaccess = new Map<string, SoftaccessGroup>()
    for (const rapport of rapports) {
      const existing = bySoftaccess.get(rapport.softaccess_id)
      if (existing) {
        existing.rapports.push(rapport)
      } else {
        bySoftaccess.set(rapport.softaccess_id, {
          softaccess_id: rapport.softaccess_id,
          softaccess_name: rapport.softaccess_name,
          rapports: [rapport],
        })
      }
    }
    return Array.from(bySoftaccess.values()).sort((a, b) =>
      a.softaccess_name.localeCompare(b.softaccess_name),
    )
  }, [rapports])

  useEffect(() => {
    if (groups.length === 0) {
      setActiveTab(undefined)
      return
    }
    setActiveTab(prev =>
      prev && groups.some(g => g.softaccess_id === prev) ? prev : groups[0].softaccess_id,
    )
  }, [groups])

  const tabItems = useMemo(
    () =>
      groups.map(group => ({
        key: group.softaccess_id,
        label: group.softaccess_name,
        children: (
          <Table<AdhocRapportRow>
            rowKey="id"
            columns={getColumns(group.rapports)}
            dataSource={group.rapports}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            size="middle"
          />
        ),
      })),
    [groups],
  )

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (groups.length === 0) {
    return <Empty description="Aucun rapport ad-hoc" />
  }

  return (
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      items={tabItems}
      size="small"
      rootClassName="adhoc-rapports-tabs"
    />
  )
}
